/**
 * getTodaySafeStartTime — drag/drop and reassign-to-tech start-time helper.
 *
 * Returns the later of (a) the technician's shift start in UTC and (b)
 * "now + 1 minute" in UTC, but ONLY when the selected day is today in the
 * shop's timezone. For any future date, the shift start is returned as-is.
 *
 * This prevents the "Cannot schedule in the past" error when a user drags
 * a ticket onto a tech's column after the shift has already started for
 * the day. It does NOT prevent scheduling into the past for ticket-creation
 * or reassign paths — those go through their own guards (EFS algorithm
 * with `noPastTime: isToday`, `useCatalystAutoAssign` clamp, ImportJobs
 * past-time snap-to-now).
 *
 * Routes through the `clock` abstraction so tests can freeze time via
 * `clock.setFixed(date)` for deterministic assertions. Mirrors the cursor
 * seeding in globalCascade.ts:382 / localCascade.ts:190.
 *
 * Extracted from src/pages/Dashboard.tsx in R33 to make the function
 * testable in isolation.
 */
import { getTodayInShopTz, shopLocalToUtc } from './shopTime';
import { clock } from './clock';

export function getTodaySafeStartTime(
  shiftTimeStr: string,
  selectedDayLabel: string,
  shopTimezone: string
): string {
  const shiftUtc = shopLocalToUtc(selectedDayLabel, shiftTimeStr, shopTimezone);
  const todayInShopTz = getTodayInShopTz(shopTimezone);

  if (selectedDayLabel !== todayInShopTz) {
    return shiftUtc;
  }

  const nowPlus1 = clock.nowMs() + 60_000;
  if (new Date(shiftUtc).getTime() >= nowPlus1) {
    return shiftUtc;
  }

  return new Date(nowPlus1).toISOString();
}
